import {
  Description,
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import { Button, LinkButton } from '@kk/ui/components/Button';
import { Icon } from '@kk/ui/components/Icon';
import { Fragment, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import type api from '#/api';
import { NavigationLinks } from '#/routes/NavigationLinks';
import { generateUrl } from '#/routes/utils/generateUrl';

type Props = {
  menuOpen: boolean;
  inRouter: boolean;
  user: api.UserDto | undefined;
  companyName: string | null | undefined;
  onClose: () => void;
  onLogOut: () => void;
};

/**
 * @url https://kommunekredit.visualstudio.com/KK%20Laaneportal/_wiki/wikis/KK-Laaneportal.wiki/1920/Sidenavigation
 */
function MobileMenu({
  menuOpen = false,
  inRouter = true,
  user,
  companyName,
  onClose,
  onLogOut,
}: Props) {
  const { t } = useTranslation();
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  return (
    <>
      <Transition show={menuOpen} as={Fragment}>
        <Dialog
          initialFocus={closeButtonRef}
          onClose={onClose}
          className="fixed z-50 max-md:inset-0 md:inset-y-0 md:right-0 md:w-127 lg:hidden"
        >
          <TransitionChild as={Fragment}>
            <DialogBackdrop
              data-testid="mobile-menu-backdrop"
              className="fixed inset-0 bg-other-100/60 transition-opacity duration-300 data-closed:opacity-0 data-enter:ease-out data-leave:ease-in"
            />
          </TransitionChild>
          <TransitionChild as={Fragment}>
            <DialogPanel className="bg-teal-5 relative z-10 flex size-full flex-col transition-transform duration-300 ease-in-out data-closed:translate-x-full">
              <DialogTitle
                as="header"
                className="flex items-center justify-between p-6"
              >
                <div className="space-y-1">
                  <h2 className="typ-headline-md leading-4 text-neutral-200 md:max-lg:relative md:max-lg:top-0.5">
                    {t('menu')}
                  </h2>
                  {companyName ? (
                    <Link
                      className="text-xs block md:hidden"
                      to={generateUrl('/organisation', {})}
                    >
                      {companyName}
                    </Link>
                  ) : null}
                </div>
                <button
                  ref={closeButtonRef}
                  className="p-2"
                  aria-label={t('closeMenu')}
                  onClick={onClose}
                >
                  <Icon type="cross" size={'medium'} className="size-6" solid />
                </button>
              </DialogTitle>
              <Description
                as="div"
                className="flex h-full grow flex-col justify-between overflow-y-auto px-6"
              >
                {inRouter ? <NavigationLinks /> : null}
                <footer className="w-full pb-6 pt-10">
                  <div className="'h-fit w-full space-y-4">
                    <p className="typ-ui-sm space-x-1 md:hidden">
                      <span className="text-neutral-100">
                        {t('navigation.loggedInAs')}
                      </span>
                      <span>{user?.name}</span>
                    </p>
                    <div className="flex gap-4">
                      <Button
                        className="kunde:h-14 flex w-full justify-center"
                        theme="default"
                        onClick={onLogOut}
                      >
                        {t('logOut')}
                      </Button>
                      <LinkButton
                        to={generateUrl('/profil', {})}
                        theme="default"
                        className="kunde:h-14 flex w-full justify-center md:hidden"
                      >
                        {t('menu.profile')}
                      </LinkButton>
                    </div>
                  </div>
                </footer>
              </Description>
            </DialogPanel>
          </TransitionChild>
        </Dialog>
      </Transition>
      {inRouter ? <MenuCloser onClose={onClose} /> : null}
    </>
  );
}

MobileMenu.displayName = 'MobileMenu';

export default MobileMenu;

function MenuCloser({ onClose }: { onClose: () => void }) {
  const { pathname } = useLocation();
  const prevPath = useRef(pathname);

  useEffect(() => {
    if (prevPath.current !== pathname) {
      onClose();
      prevPath.current = pathname;
    }
  }, [pathname, onClose]);
  return null;
}
